import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'react-resource-router';

import { useFlag } from 'src/hooks/flag';
import { createHttpRequest } from 'src/utils/http-request';

import { usePollingResults } from '../../hooks';
import { urls } from '../../urls';

import { messages } from './rich-merge-checks-list.i18n';

type RetryMergeChecksProps = {
  retriedResultUuids: Set<string>;
  retryCheck: (resultUuid?: string) => Promise<void>;
};

type StringCallback = (value: string) => void;

export type MergeResultsPopupMouseCallbacks = {
  onMouseEnterPopup: StringCallback;
  onMouseLeavePopup: StringCallback;
  onMouseEnterTrigger: StringCallback;
  onMouseLeaveTrigger: StringCallback;
};

type MergeResultsPopupProps = {
  keyPopupIsOpenFor?: string;
  showPopup: (key?: string) => void;
  hidePopup: () => void;
  mouseCallbacks: MergeResultsPopupMouseCallbacks;
};

export const useRetryMergeChecks = (): RetryMergeChecksProps => {
  const [retriedResultUuids, setRetriedResultUuids] = useState(
    new Set<string>()
  );

  const { showErrorFlag } = useFlag({ enableForPdvs: true });
  const { refresh } = usePollingResults();
  const [
    {
      match: { params },
    },
  ] = useRouter();

  const retryCheck = async (resultUuid?: string) => {
    if (resultUuid === undefined) {
      throw new Error('Cannot retry an IMPENDING check');
    }

    const { pullRequestId, repositoryOwner, repositorySlug } = params;
    if (!repositoryOwner || !repositorySlug || !pullRequestId) {
      throw new Error(
        'Retrying a check can only be initiated on a route that includes all :repositoryOwner, :repositorySlug and :pullRequestId'
      );
    }

    setRetriedResultUuids(prevSet => new Set(prevSet).add(resultUuid));

    try {
      await createHttpRequest('POST', {
        successCustomHandler: () => null, // don't parse 204 response as JSON
      })(
        urls.retry(repositoryOwner, repositorySlug, pullRequestId, resultUuid),
        {}
      );
    } catch (error) {
      showErrorFlag({ msg: messages.failedToRetry });
      setRetriedResultUuids(prevSet => {
        const newSet = new Set(prevSet);
        newSet.delete(resultUuid);
        return newSet;
      });
    }

    refresh();
  };

  return {
    retriedResultUuids,
    retryCheck,
  };
};

/**
 * Hook that encapsulates logic for showing a popup when hovering on a
 * ForgeMergeCheckItem in the MergeChecksCard component. Ensures only
 * one popup is shown at a time, and adds a small delay when hiding the popup
 * to allow moving between the trigger component and the Popup.
 *
 * @param hideDelayMs How long to wait after the pointer is not located inside
 * the trigger or popup elements before hiding the popup. Value is in
 * milliseconds.
 */
export const useMergeResultsPopupProps = (
  hideDelayMs: number
): MergeResultsPopupProps => {
  const [keyPopupIsOpenFor, setKeyPopupIsOpenFor] = useState<
    string | undefined
  >();
  // set when mouse enters the trigger element, undefined when it leaves
  const [keyMouseEnteredTriggerFor, setKeyMouseEnteredTriggerFor] = useState<
    string | undefined
  >();
  // set when mouse enters the popup element, undefined when it leaves
  const [keyMouseEnteredPopupFor, setKeyMouseEnteredPopupFor] = useState<
    string | undefined
  >();

  const showPopup = useCallback((key?: string) => {
    setKeyMouseEnteredPopupFor(undefined);
    setKeyMouseEnteredTriggerFor(key);
  }, []);

  const hidePopup = useCallback(() => {
    setKeyMouseEnteredPopupFor(undefined);
    setKeyMouseEnteredTriggerFor(undefined);
    setKeyPopupIsOpenFor(undefined);
  }, []);

  useEffect(() => {
    // Show the popup
    if (keyMouseEnteredTriggerFor === undefined) {
      return;
    }

    if (
      keyPopupIsOpenFor === undefined ||
      keyPopupIsOpenFor !== keyMouseEnteredTriggerFor
    ) {
      setKeyPopupIsOpenFor(keyMouseEnteredTriggerFor);
    }
    // We only want this effect to run when the mouse enters/leaves a trigger
    // element, so no exhaustive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyMouseEnteredTriggerFor]);

  useEffect(() => {
    // Clear the popup
    let isCancelled = false;

    const update = () => {
      setTimeout(() => {
        if (
          // effect hasn't run since we triggered the delayed hide
          !isCancelled &&
          // we're showing a popup
          keyPopupIsOpenFor !== undefined &&
          // we're not in a trigger element
          keyMouseEnteredTriggerFor === undefined &&
          // we're not in a popup
          keyMouseEnteredPopupFor === undefined
        ) {
          setKeyPopupIsOpenFor(undefined);
        }
      }, hideDelayMs);
    };

    update();

    return () => {
      isCancelled = true;
    };
    // Similar to the above effect, this should only run when the mouse
    // enters/leaves a trigger or a popup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyMouseEnteredTriggerFor, keyMouseEnteredPopupFor]);

  const onMouseEnterTrigger = useCallback((key: string) => {
    setKeyMouseEnteredTriggerFor(key);
  }, []);

  const onMouseLeaveTrigger = useCallback(() => {
    setKeyMouseEnteredTriggerFor(undefined);
  }, []);

  const onMouseEnterPopup = useCallback((key: string) => {
    setKeyMouseEnteredPopupFor(key);
  }, []);

  const onMouseLeavePopup = useCallback(() => {
    setKeyMouseEnteredPopupFor(undefined);
  }, []);

  return {
    keyPopupIsOpenFor,
    showPopup,
    hidePopup,
    mouseCallbacks: {
      onMouseEnterPopup,
      onMouseLeavePopup,
      onMouseEnterTrigger,
      onMouseLeaveTrigger,
    },
  };
};
